import React, { createRef } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import Checkbox from '@mui/material/Checkbox';

import Loading from 'shared/components/Loading';
import TopBar from 'app/components/TopBar';

import config from 'shared/config/config';
import messages, { FormattedMessage } from 'shared/messages/messages';

import UserService from 'shared/services/UserService';

import {
  me,
  login
} from 'app/actions/kioskActions';

import {
  getLocaleSettings
} from 'app/selectors/kioskSelectors';

import {
  ACCESS_CODE_LOGIN_TYPE,
  EMAIL_LOGIN_TYPE,
  CLUB_MED_LOGIN_TYPE,
  KIOSK_BACKGROUND_URL
} from 'app/utils/Constants';

import {
  trackEvent
} from 'shared/services/Analytics';

import {
  HPX_THEME
} from 'shared/config/themes';

const userService = new UserService();

const styles = () => ({
  wrapper: {
    position: 'absolute',
    display: 'block',
    width: '100vw',
    height: '100vh',
    background: config.kiosk_bg_color,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover'
  },
  form: {
    width: '400px',
    maxWidth: '90%',
    margin: '5rem auto 0',
    padding: '2rem',
    border: '5px solid ' + config.kiosk_form_border_color,
    borderRadius: '8px',
    boxShadow: '0 0 3px rgba(0, 0, 0, 0.5)',
    background: config.kiosk_form_bg_color
  },
  header: {
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 500,
    color: config.kiosk_form_header_font_color,
    fontFamily: config.main_font
  },
  message: {
    textAlign: 'center',
    fontSize: '18px',
    color: config.kiosk_form_message_font_color,
    fontFamily: config.sub_font
  },
  error: {
    fontFamily: config.sub_font,
    textAlign: 'center',
    fontSize: '1rem',
    color: 'red'
  },
  inputGroup: {
    margin: '1.5rem 0 2.5rem'
  },
  clubMedInputGroup: {
    margin: '1rem 0'
  },
  label: {
    width: '100%',
    fontSize: '14px',
    textAlign: 'left',
    color: config.kiosk_form_label_font_color,
    fontFamily: config.sub_font,
    fontWeight: config.kiosk_form_label_font_weight
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '3px',
    border: '1px solid #aaa',
    fontWeight: 600,
    fontSize: '18px',
    textAlign: 'center',
    background: config.kiosk_form_access_code_input_bg_color,
    color: config.kiosk_form_access_code_input_font_color,
    border: config.kiosk_form_access_code_input_border,
    fontFamily: config.root_font_family
  },
  button: {
    margin: '0.5rem auto 0',
    textAlign: 'center',
    padding: '0.5rem 1.3rem',
    fontWeight: 600,
    background: config.kiosk_form_button_bg_color,
    borderRadius: '3px',
    color: config.kiosk_form_button_font_color,
    border: config.kiosk_form_button_border_color,
    fontFamily: config.root_font_family
  },
  secondButton: {
    margin: '0 auto',
    textAlign: 'center',
    padding: '0.5rem 1.3rem',
    fontWeight: 600,
    background: 'linear-gradient(#ffffff, #f1f1f1)',
    borderRadius: '3px',
    border: '1px solid #dcdcdc',
    boxShadow: '0 0 2px #dcdcdc',
    color: '#2b8eb0',
    fontFamily: config.root_font_family
  },
  loadingIcon: {
    fontSize: '1rem',
    marginRight: '5px'
  },
  or: {
    margin: '0.5rem 0',
    fontFamily: config.root_font_family
  },
  legalNotesLabel: {
    color: 'white',
    marginBottom: '0px',
    fontSize: '9px',
    fontFamily: config.sub_font
  },
  legalNotesPanel: {
    position: 'fixed',
    zIndex: 9999,
    padding: '8px',
    textAlign: 'center',
    left: 0,
    right: 0,
    bottom: 0,
    background: '#101820'
  },
  optInMessage: {
    display: 'inline'
  },
});

class Login extends React.Component {
  constructor(props) {
    super(props);

    this.codeInput = createRef();
    this.roomInput = createRef();
    this.nameInput = createRef();
    this.emailInput = createRef();
    this.passwordInput = createRef();

    this.optedIn = this.props.settings.opt_in_checked;

    this.optIn = {
      defaultChecked: this.props.settings.opt_in_checked,
      size: "small",
      onChange: e => {
        this.optedIn = !this.optedIn;
      }
    };

    this.state = {
      loading: true,
      submitting: false,
      error: false,
      isViewPhotos: false
    }
  }

  componentDidMount() {
    const isViewPhotos = localStorage.getItem('VIEW_PHOTOS');
    this.setState({
      isViewPhotos: isViewPhotos === 'false' || !isViewPhotos ? false : true
    })
    this.auth();
  }

  componentDidUpdate() {
    this.focusInput();
  }

  auth() {
    this.props.me(this.props.settings.token)
      .then(() => {
        this.setState({
          loading: false
        });
      })
      .catch(() => {
        this.setState({
          loading: false
        });
      });
  }

  focusInput() {
    const { settings } = this.props;
    if (!settings) return;

    switch (settings.login_type) {
      case CLUB_MED_LOGIN_TYPE:
        if (this.roomInput.current && !this.roomInput.current.value) {
          this.roomInput.current.focus();
          break;
        }
        if (this.nameInput.current && !this.nameInput.current.value) {
          this.nameInput.current.focus();
          break;
        }
        if (this.emailInput.current && !this.emailInput.current.value) {
          this.emailInput.current.focus();
          break;
        }

      default:
        if (this.codeInput.current && !this.codeInput.current.value) {
          this.codeInput.current.focus();
        }
    }
  }

  validateEmail(email) {
    let errorMsg = null;
    if (!email) {
      return messages.error_email_required
    }

    if (!email.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/)) {
      return messages.error_email_invalid
    }

    return errorMsg
  }

  login(event) {
    event.preventDefault();

    this.setState({
      submitting: true,
      error: null
    });

    const { settings } = this.props;
    let params = {};
    let emailError = null;

    if (this.state.isViewPhotos) {
      params.email = this.codeInput.current.value;
      params.password = this.passwordInput.current.value;

      emailError = this.validateEmail(params.email);

      if (emailError) {
        this.setState({
          submitting: false,
          error: <FormattedMessage {...emailError} />
        });
        return;
      }

    } else {

      switch (settings.login_type) {
        case ACCESS_CODE_LOGIN_TYPE:
          this.parseQrCodeCard();
          params.access_code = this.codeInput.current.value;

          if (settings.set_mandatory_email) {
            params.email = this.emailInput.current.value;
            params.promotions_opt_in = this.optedIn;

            emailError = this.validateEmail(params.email);
            if (emailError) {
              this.setState({
                submitting: false,
                error: <FormattedMessage {...emailError} />
              });
              return;
            }
          }

          break;

        case EMAIL_LOGIN_TYPE:
          params.email = this.codeInput.current.value;

          emailError = this.validateEmail(params.email);
          if (emailError) {
            this.setState({
              submitting: false,
              error: <FormattedMessage {...emailError} />
            });
            return;
          }

          break;

        case CLUB_MED_LOGIN_TYPE:
          params.room = this.roomInput.current.value;
          params.name = this.nameInput.current.value;
          params.email = this.emailInput.current.value;

          // allowing blank name for workaround / fake room hotfix
          //if (!params.room || !params.name || !params.email) {
          if (!params.room || !params.email) {
            this.setState({
              submitting: false,
              error: <FormattedMessage {...messages.error_fields_required} />
            });
            return;
          }

          emailError = this.validateEmail(params.email);
          if (emailError) {
            this.setState({
              submitting: false,
              error: <FormattedMessage {...emailError} />
            });
            return;
          }
      }
    }


    this.props.login(this.props.settings.token, params)
      .then(user => {

        this.setState({
          submitting: false
        });

        trackEvent('Kiosk.Login', {
          userId: user.id,
          storyId: user.story_id,
          kiosk: this.props.settings.token
        });
      })
      .catch(err => {
        console.error(err);

        let errorMsg;
        if (config.customer === 'hpx') {
          // todo: move to kiosk setting or base on scans being enabled
          errorMsg = messages.error_scan_band;

        } else if (settings.login_type === CLUB_MED_LOGIN_TYPE) {
          errorMsg = messages.error_reservation_not_found;

        } else {
          errorMsg = messages.error_see_attendant;
        }

        let msg = <FormattedMessage {...errorMsg} />;

        this.setState({
          submitting: false,
          error: msg
        });
      });
  }

  parseQrCodeCard() {
    if (this.codeInput.current.value && (
      this.codeInput.current.value.includes("http://") || this.codeInput.current.value.includes("https://"))
    ) {
      var code = this.codeInput.current.value;
      const codeStrIndex = code.indexOf("=");
      code = code.substring(codeStrIndex + 1, this.codeInput.current.value.length);
      this.codeInput.current.value = code;
    }
  }

  renderAccessCodeLogin() {
    const { classes, settings, locale, localeSettings } = this.props;

    let backgroundStyle = { backgroundImage: `url(${KIOSK_BACKGROUND_URL(settings.token, locale)})` }

    let icon = null;
    if (this.state.submitting) {
      icon = <FontAwesomeIcon icon={faCircleNotch} spin className={classes.loadingIcon} />;
    }

    let login_message = localeSettings.login_message;
    let opt_in_message = localeSettings.opt_in_message;

    let error = null;
    if (this.state.error) {
      error = (
        <p className={classes.error}>
          {this.state.error}
        </p>
      );
    }

    let email_input = null;
    let opt_input = null;
    if (settings.set_mandatory_email) {
      if (opt_in_message) {
        opt_input = (
          <Checkbox {...this.optIn} />
        )
      }

      email_input = (
        <div className={classes.inputGroup}>
          <label className={classes.label}>
            <FormattedMessage {...messages.form_label_email} />
          </label>
          <input id="email" ref={this.emailInput} type="text" className={classes.input} autocomplete="false" autofocus />
          { opt_input }
          <label for="opt_in" className={classes.optInMessage}> { opt_in_message } </label>
        </div>
      )
    }

    let headerStyle = {};
    if (settings.theme === HPX_THEME) {
      headerStyle.textTransform = 'uppercase';
    }

    return (
      <div className={classes.wrapper} style={backgroundStyle}>
        <TopBar />
        <form className={classes.form}>
          <p className={classes.header} style={headerStyle}>
            <FormattedMessage {...messages.kiosk_login_header_access_your_images} />
          </p>
          <p className={classes.message}>
            {login_message || <FormattedMessage {...messages.kiosk_login_instructions_scan_band} />}
          </p>
          {error}
          <div className={classes.inputGroup}>
            <label className={classes.label}>
              <FormattedMessage {...messages.form_label_secret_code} />
            </label>
            <input ref={this.codeInput} type="text" className={classes.input} autocomplete="false" autofocus />
          </div>
          { email_input }
          <button type="submit" onClick={this.login.bind(this)} className={classes.button}>
            {icon}
            <FormattedMessage {...messages.button_enter} />
          </button>
        </form>
        {
          config.customer === 'hpx' &&
          <div className={classes.legalNotesPanel}>
            <p className={classes.legalNotesLabel}>
              <FormattedMessage {...messages.kiosk_login_hpx_footer_text} />
            </p>
          </div>
        }
      </div>
    );
  }

  renderEmailLogin() {
    const { classes, settings, locale } = this.props;

    let backgroundStyle = { backgroundImage: `url(${KIOSK_BACKGROUND_URL(settings.token, locale)})` }

    let icon = null;
    if (this.state.submitting) {
      icon = (<FontAwesomeIcon icon={faCircleNotch} spin className={classes.loadingIcon} />);
    }

    let error = null;
    if (this.state.error) {
      error = (
        <p className={classes.error}>
          {this.state.error}
        </p>
      );
    }

    let inputStyle = {};
    if (this.state.error) {
      inputStyle.border = '1px solid red';
    }

    return (
      <div className={classes.wrapper} style={backgroundStyle}>
        <TopBar />
        <form className={classes.form}>
          <p className={classes.header}>
            <FormattedMessage {...messages.kiosk_login_header_welcome} />
          </p>
          <p className={classes.message}>
            <FormattedMessage {...messages.kiosk_login_instructions_enter_email} />
          </p>
          {error}
          <div className={classes.inputGroup}>
            <label className={classes.label}>
              <FormattedMessage {...messages.form_label_email_address} />
            </label>
            <input ref={this.codeInput} type="email" className={classes.input} style={inputStyle} autocomplete="false" autofocus />
          </div>
          <button type="submit" onClick={this.login.bind(this)} className={classes.button}>
            {icon}
            <span>
              <FormattedMessage {...messages.button_continue} />
            </span>
          </button>
        </form>
      </div>
    )
  }

  renderViewPhotosEmailLogin() {
    const { classes, settings, locale } = this.props;

    let backgroundStyle = { backgroundImage: `url(${KIOSK_BACKGROUND_URL(settings.token, locale)})` }

    let icon = null;
    if (this.state.submitting) {
      icon = (<FontAwesomeIcon icon={faCircleNotch} spin className={classes.loadingIcon} />);
    }

    let error = null;
    if (this.state.error) {
      error = (
        <p className={classes.error}>
          {this.state.error}
        </p>
      );
    }

    let inputStyle = {};
    if (this.state.error) {
      inputStyle.border = '1px solid red';
    }

    return (
      <div className={classes.wrapper} style={backgroundStyle}>
        <TopBar />
        <form className={classes.form}>
          <p className={classes.header}>
            <FormattedMessage {...messages.kiosk_login_header_preprint} />
          </p>
          <p className={classes.message}>
            <FormattedMessage {...messages.kiosk_login_instructions_enter_email} />
          </p>
          {error}
          <div className={classes.inputGroup}>
            <label className={classes.label}>
              <FormattedMessage {...messages.form_label_email_address} />
            </label>
            <input ref={this.codeInput} type="email" className={classes.input} style={inputStyle} autocomplete="false" autofocus />
          </div>
          <div className={classes.inputGroup}>
            <label className={classes.label}>
              <FormattedMessage {...messages.form_label_password} />
            </label>
            <input ref={this.passwordInput} type="password" className={classes.input} style={inputStyle} autocomplete="false" autofocus />
          </div>
          <button type="submit" onClick={this.login.bind(this)} className={classes.button}>
            {icon}
            <span>
              <FormattedMessage {...messages.button_continue} />
            </span>
          </button>
          <br/>
          <button type="submit" onClick={()=> { localStorage.setItem('VIEW_PHOTOS', false); window.location.reload() } } className={classes.button}>
            <span>
              <FormattedMessage {...messages.button_customer_login} />
            </span>
          </button>
        </form>
      </div>
    )
  }

  renderClubMedLogin() {
    const { classes, settings, locale } = this.props;

    let backgroundStyle = { backgroundImage: `url(${KIOSK_BACKGROUND_URL(settings.token, locale)})` }

    let icon = null;
    if (this.state.submitting) {
      icon = (<FontAwesomeIcon icon={faCircleNotch} spin className={classes.loadingIcon} />);
    }

    let error = null;
    if (this.state.error) {
      error = (
        <p className={classes.error}>
          {this.state.error}
        </p>
      );
    }

    let inputStyle = {
      fontSize: '16px'
    }

    let btnStyle = {
      fontSize: '16px'
    }

    return (
      <div className={classes.wrapper} style={backgroundStyle}>
        <TopBar />
        <form className={classes.form}>
          <p className={classes.message}>
            <FormattedMessage {...messages.kiosk_login_header_clubmed} />
          </p>
          {error}
          <input type="hidden" name="hotfix" value="true" />
          <div className={classes.clubMedInputGroup}>
            <label className={classes.label}>
              <FormattedMessage {...messages.form_label_room_number} />
            </label>
            <input ref={this.roomInput} type="text" className={classes.input} style={inputStyle} autocomplete="false" autofocus />
          </div>
          <div className={classes.clubMedInputGroup}>
            <label className={classes.label}>
              <FormattedMessage {...messages.form_label_last_name} />
            </label>
            <input ref={this.nameInput} type="text" className={classes.input} style={inputStyle} autocomplete="false" autofocus />
          </div>
          <div className={classes.clubMedInputGroup}>
            <label className={classes.label}>
              <FormattedMessage {...messages.form_label_email} />
            </label>
            <input ref={this.emailInput} type="email" className={classes.input} style={inputStyle} autocomplete="false" autofocus />
          </div>
          <button type="submit" onClick={this.login.bind(this)} className={classes.button} style={btnStyle}>
            {icon}
            <span>
              <FormattedMessage {...messages.button_lookup_gallery} />
            </span>
          </button>
        </form>
      </div>
    )
  }

  render() {

    if (this.state.loading) {
      return (<Loading />);
    }

    if (this.props.loggedIn) {
      return (<Redirect to={`/${this.props.settings.token}`} />);
    }

    const { settings } = this.props;

    if (this.state.isViewPhotos)
      return this.renderViewPhotosEmailLogin();

    switch (settings.login_type) {
      case ACCESS_CODE_LOGIN_TYPE:
        return this.renderAccessCodeLogin();

      case EMAIL_LOGIN_TYPE:
        return this.renderEmailLogin();

      case CLUB_MED_LOGIN_TYPE:
        return this.renderClubMedLogin();

      default:
        return null;
    }
  }
}

const mapStateToProps = state => ({
  settings: state.kioskReducer.settings,
  locale: state.messagesReducer.locale,
  loggedIn: state.kioskReducer.loggedIn,
  localeSettings: getLocaleSettings(state),
  terminal: state.stripeReducer.terminal
});

export default connect(mapStateToProps, { me, login })(withStyles(styles)(withRouter(Login)));
